/**
 * CartPage — vista de carrito como página completa.
 *
 * El `CartDrawer` (slide-over) sigue siendo el atajo desde cualquier parte
 * de la app, pero Culqi requiere un FLUJO de compra dedicado y accesible
 * por URL — esta página lo provee. Reutiliza `useCartStore` + `useBuyPlan`.
 */

import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  CreditCard, Loader2, Minus, Plus, ShoppingCart, Trash2,
} from 'lucide-react';
import { Brand } from '@/components/Brand';
import { Button } from '@/components/ui/Button';
import { SiteFooter } from '@/components/layout/SiteFooter';
import { useBuyPlan } from '@/features/billing/useBuyPlan';
import { formatPrice, type CheckoutResponse } from '@/features/billing/types';
import {
  cartTotalCents, useCartStore, type CartItem,
} from '@/stores/cartStore';
import { useAuthStore } from '@/stores/authStore';

export function CartPage() {
  const items = useCartStore((s) => s.items);
  const remove = useCartStore((s) => s.remove);
  const setQty = useCartStore((s) => s.setQuantity);
  const clear = useCartStore((s) => s.clear);

  const isLoggedIn = !!useAuthStore((s) => s.token);
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const buyPlan = useBuyPlan();

  const [selectedIds, setSelectedIds] = useState<Set<number>>(
    () => new Set(items.map((it) => it.plan.id)),
  );
  const [acceptedTerms, setAcceptedTerms] = useState(true);
  const [completed, setCompleted] = useState<CheckoutResponse[] | null>(null);

  const selectedItems = items.filter((it) => selectedIds.has(it.plan.id));
  const total = useMemo(
    () => cartTotalCents(selectedItems),
    [selectedItems],
  );
  const allChecked = items.length > 0 && selectedItems.length === items.length;

  function toggleAll() {
    if (allChecked) setSelectedIds(new Set());
    else setSelectedIds(new Set(items.map((it) => it.plan.id)));
  }
  function toggleOne(planId: number) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(planId)) next.delete(planId);
      else next.add(planId);
      return next;
    });
  }

  async function onCheckout() {
    if (!isLoggedIn) {
      navigate('/login?next=' + encodeURIComponent('/carrito'));
      return;
    }
    if (!acceptedTerms || selectedItems.length === 0) return;
    const results: CheckoutResponse[] = [];
    for (const item of selectedItems) {
      for (let i = 0; i < item.quantity; i++) {
        const r = await buyPlan.buy(item.plan);
        if (!r) return;       // stop on first decline
        results.push(r);
      }
    }
    setCompleted(results);
    // Remove only paid items from cart
    for (const it of selectedItems) remove(it.plan.id);
  }

  return (
    <div
      className="flex min-h-screen flex-col"
      style={{ background: 'var(--surface-base)', color: 'var(--text-primary)' }}
    >
      {/* Header */}
      <header
        className="sticky top-0 z-30 backdrop-blur-md"
        style={{
          background: 'rgba(2,6,23,0.7)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-6">
          <Link to="/" className="no-underline">
            <Brand size="md" />
          </Link>
          <nav className="flex items-center gap-2">
            <Link to="/planes" className="no-underline">
              <Button variant="secondary" size="sm">
                Planes
              </Button>
            </Link>
            {isLoggedIn ? (
              <Link to="/app/dashboard" className="no-underline">
                <Button variant="ghost" size="sm">
                  Ir al panel
                </Button>
              </Link>
            ) : (
              <Link to="/login" className="no-underline">
                <Button variant="ghost" size="sm">
                  Iniciar sesión
                </Button>
              </Link>
            )}
          </nav>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl flex-1 px-6 py-10">
        <h1
          className="mb-1 text-2xl"
          style={{
            fontFamily: 'var(--font-display)',
            fontWeight: 700,
            letterSpacing: '-0.02em',
          }}
        >
          Carrito
        </h1>
        <p className="mb-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
          Selecciona los planes y cantidades, y finaliza tu pedido.
        </p>

        {completed ? (
          <CheckoutSuccess results={completed} />
        ) : items.length === 0 ? (
          <EmptyCart />
        ) : (
          <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
            {/* Tabla */}
            <div
              className="overflow-hidden rounded-[var(--radius-lg)] border"
              style={{
                background: 'var(--surface-1)',
                borderColor: 'var(--border-default)',
              }}
            >
              <div
                className="flex items-center justify-between gap-3 border-b px-4 py-3"
                style={{ borderColor: 'var(--border-subtle)' }}
              >
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    className="h-4 w-4 cursor-pointer accent-[var(--accent)]"
                  />
                  <span style={{ color: 'var(--text-secondary)' }}>
                    Seleccionar todo
                  </span>
                </label>
                <div className="flex items-center gap-3 text-[12px]" style={{ color: 'var(--text-muted)' }}>
                  <span>Seleccionados: <b style={{ color: 'var(--text-primary)' }}>{selectedItems.length}</b></span>
                  <button
                    type="button"
                    onClick={() => clear()}
                    className="inline-flex items-center gap-1 rounded-[var(--radius-sm)] border px-2 py-1 hover:bg-[var(--surface-2)]"
                    style={{
                      borderColor: 'var(--border-default)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    <Trash2 size={11} /> Vaciar carrito
                  </button>
                </div>
              </div>

              <table className="w-full text-[13px]">
                <thead>
                  <tr style={{ background: 'var(--surface-2)' }}>
                    <Th />
                    <Th>Plan</Th>
                    <Th right>Precio</Th>
                    <Th center>Cantidad</Th>
                    <Th right>Subtotal</Th>
                    <Th />
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => {
                    const checked = selectedIds.has(it.plan.id);
                    const sub = it.plan.price_cents * it.quantity;
                    const isSub = it.plan.is_subscription;
                    return (
                      <tr
                        key={it.plan.id}
                        style={{ borderTop: '1px solid var(--border-subtle)' }}
                      >
                        <td className="px-3 py-3">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOne(it.plan.id)}
                            className="h-4 w-4 cursor-pointer accent-[var(--accent)]"
                          />
                        </td>
                        <td className="px-3 py-3">
                          <div style={{ color: 'var(--text-primary)', fontWeight: 600 }}>
                            {it.plan.name}
                          </div>
                          <div className="text-[11px]" style={{ color: 'var(--text-muted)' }}>
                            {isSub
                              ? `Suscripción · ${it.plan.duration_days} días`
                              : `${it.plan.tokens} fierros`}
                          </div>
                        </td>
                        <td
                          className="px-3 py-3 text-right font-mono"
                          style={{ color: 'var(--text-secondary)' }}
                        >
                          {formatPrice(it.plan.price_cents, it.plan.currency)}
                        </td>
                        <td className="px-3 py-3">
                          <div className="flex items-center justify-center gap-1">
                            <button
                              type="button"
                              disabled={isSub || it.quantity <= 1}
                              onClick={() => setQty(it.plan.id, it.quantity - 1)}
                              className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] border disabled:cursor-not-allowed disabled:opacity-30"
                              style={{
                                background: 'var(--surface-2)',
                                borderColor: 'var(--border-default)',
                                color: 'var(--text-secondary)',
                              }}
                            >
                              <Minus size={11} />
                            </button>
                            <input
                              type="number"
                              min={1}
                              value={it.quantity}
                              disabled={isSub}
                              onChange={(e) => {
                                const v = parseInt(e.target.value, 10);
                                if (Number.isFinite(v)) setQty(it.plan.id, v);
                              }}
                              className="h-7 w-14 rounded-[var(--radius-sm)] border bg-transparent text-center font-mono text-[12px] focus:outline-none disabled:opacity-50"
                              style={{
                                borderColor: 'var(--border-default)',
                                color: 'var(--text-primary)',
                              }}
                            />
                            <button
                              type="button"
                              disabled={isSub}
                              onClick={() => setQty(it.plan.id, it.quantity + 1)}
                              className="flex h-7 w-7 items-center justify-center rounded-[var(--radius-sm)] border disabled:cursor-not-allowed disabled:opacity-30"
                              style={{
                                background: 'var(--surface-2)',
                                borderColor: 'var(--border-default)',
                                color: 'var(--text-secondary)',
                              }}
                            >
                              <Plus size={11} />
                            </button>
                          </div>
                        </td>
                        <td
                          className="px-3 py-3 text-right font-mono"
                          style={{ color: 'var(--text-primary)', fontWeight: 600 }}
                        >
                          {formatPrice(sub, it.plan.currency)}
                        </td>
                        <td className="px-3 py-3 text-right">
                          <button
                            type="button"
                            onClick={() => {
                              remove(it.plan.id);
                              setSelectedIds((prev) => {
                                const n = new Set(prev);
                                n.delete(it.plan.id);
                                return n;
                              });
                            }}
                            className="rounded-[var(--radius-pill)] p-1.5 transition-colors hover:bg-[var(--danger)] hover:text-white"
                            style={{ color: 'var(--danger)' }}
                            title="Quitar del carrito"
                          >
                            <Trash2 size={12} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  <tr
                    style={{
                      borderTop: '1px solid var(--border-default)',
                      background: 'var(--surface-2)',
                    }}
                  >
                    <td colSpan={4} className="px-3 py-3 text-right text-[12px]" style={{ color: 'var(--text-muted)' }}>
                      Total seleccionado
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-base" style={{ color: 'var(--accent)', fontWeight: 700 }}>
                      {formatPrice(total, items[0]?.plan.currency || 'PEN')}
                    </td>
                    <td />
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Payment box */}
            <aside
              className="self-start rounded-[var(--radius-lg)] border p-5"
              style={{
                background: 'var(--surface-1)',
                borderColor: 'var(--border-default)',
              }}
            >
              <p className="text-[12.5px]" style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                Paga en línea con{' '}
                <b style={{ color: 'var(--text-primary)' }}>Yape/Plin</b>,{' '}
                <b style={{ color: 'var(--text-primary)' }}>PagoEfectivo</b> o{' '}
                <b style={{ color: 'var(--text-primary)' }}>Tarjeta (Culqi)</b>.
                Las suscripciones requieren tarjeta.
              </p>

              <div
                className="my-4 flex items-baseline justify-between rounded-[var(--radius-md)] border px-3 py-3"
                style={{
                  background: 'var(--surface-2)',
                  borderColor: 'var(--border-default)',
                }}
              >
                <span className="text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>
                  Total a pagar
                </span>
                <span
                  className="font-mono text-xl"
                  style={{ color: 'var(--accent)', fontWeight: 700 }}
                >
                  {formatPrice(total, items[0]?.plan.currency || 'PEN')}
                </span>
              </div>

              <label className="mb-4 flex items-start gap-2 text-[11.5px]" style={{ color: 'var(--text-muted)', lineHeight: 1.45 }}>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  className="mt-0.5 h-3.5 w-3.5 cursor-pointer accent-[var(--accent)]"
                />
                <span>
                  Acepto los{' '}
                  <Link
                    to="/legal/terms"
                    className="underline"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    términos
                  </Link>{' '}
                  y la{' '}
                  <Link
                    to="/legal/privacy"
                    className="underline"
                    style={{ color: 'var(--text-secondary)' }}
                  >
                    política de privacidad
                  </Link>
                  .
                </span>
              </label>

              {buyPlan.error && (
                <div
                  className="mb-3 rounded-[var(--radius-sm)] border px-3 py-2 text-[11.5px]"
                  style={{
                    background: 'rgba(239,68,68,0.10)',
                    borderColor: 'var(--danger)',
                    color: 'var(--danger)',
                  }}
                >
                  {buyPlan.error}
                </div>
              )}

              <button
                type="button"
                onClick={onCheckout}
                disabled={
                  selectedItems.length === 0
                  || !acceptedTerms
                  || buyPlan.busyPlanId !== null
                  || !buyPlan.ready
                }
                className="flex h-11 w-full items-center justify-center gap-2 rounded-[var(--radius-md)] text-[14px] font-semibold text-white transition-opacity disabled:cursor-not-allowed disabled:opacity-50"
                style={{
                  background: 'linear-gradient(135deg, #10b981 0%, #06b6d4 100%)',
                  boxShadow: '0 6px 20px rgba(16,185,129,0.25)',
                }}
              >
                {buyPlan.busyPlanId !== null ? (
                  <>
                    <Loader2 size={14} className="animate-spin" />
                    Procesando…
                  </>
                ) : (
                  <>
                    <CreditCard size={14} strokeWidth={2} />
                    Comprar
                  </>
                )}
              </button>

              {buyPlan.busyPlanId !== null && (
                <button
                  type="button"
                  onClick={() => buyPlan.cancel()}
                  className="mt-2 w-full text-center text-[11.5px] underline-offset-2 hover:underline"
                  style={{ color: 'var(--text-muted)' }}
                >
                  ¿Se quedó colgado? Cancelar
                </button>
              )}

              <Link to="/planes" className="no-underline">
                <Button variant="secondary" size="md" className="mt-2 w-full">
                  Ir a planes
                </Button>
              </Link>

              {!isLoggedIn && (
                <p
                  className="mt-3 text-center text-[11px]"
                  style={{ color: 'var(--text-muted)' }}
                >
                  Para comprar primero{' '}
                  <Link to="/login" className="underline" style={{ color: 'var(--accent)' }}>
                    inicia sesión
                  </Link>
                  .
                </p>
              )}
              {isLoggedIn && user && (
                <p
                  className="mt-3 text-center text-[11px]"
                  style={{ color: 'var(--text-muted)' }}
                >
                  Comprando como <b style={{ color: 'var(--text-secondary)' }}>{user.email}</b>
                </p>
              )}
            </aside>
          </div>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}

function Th({
  children, right = false, center = false,
}: {
  children?: React.ReactNode;
  right?: boolean;
  center?: boolean;
}) {
  const align = right ? 'text-right' : center ? 'text-center' : 'text-left';
  return (
    <th
      className={`px-3 py-2.5 text-[10px] font-semibold uppercase tracking-wider ${align}`}
      style={{ color: 'var(--text-muted)' }}
    >
      {children}
    </th>
  );
}

function EmptyCart() {
  return (
    <div
      className="mx-auto max-w-md rounded-[var(--radius-lg)] border p-12 text-center"
      style={{ background: 'var(--surface-1)', borderColor: 'var(--border-default)' }}
    >
      <div
        className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full"
        style={{ background: 'rgba(245,158,11,0.10)', color: 'var(--accent)' }}
      >
        <ShoppingCart size={26} strokeWidth={1.6} />
      </div>
      <h2
        className="mb-2 text-base"
        style={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}
      >
        Tu carrito está vacío
      </h2>
      <p className="mb-5 text-[12.5px]" style={{ color: 'var(--text-secondary)' }}>
        Explora los planes para agregar packs de fierros o una suscripción.
      </p>
      <Link to="/planes" className="no-underline">
        <Button variant="primary" size="md">
          Ver planes
        </Button>
      </Link>
    </div>
  );
}

function CheckoutSuccess({ results }: { results: CheckoutResponse[] }) {
  return (
    <div
      className="mx-auto max-w-lg rounded-[var(--radius-lg)] border p-8 text-center"
      style={{
        background: 'rgba(16,185,129,0.08)',
        borderColor: 'rgba(16,185,129,0.30)',
      }}
    >
      <div
        className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full"
        style={{ background: 'rgba(16,185,129,0.20)', color: '#10b981' }}
      >
        ✓
      </div>
      <h2
        className="mb-1 text-lg"
        style={{ fontFamily: 'var(--font-display)', fontWeight: 700, color: '#10b981' }}
      >
        Pago exitoso
      </h2>
      <p className="mb-4 text-[13px]" style={{ color: 'var(--text-secondary)' }}>
        {results.length} cargo{results.length === 1 ? '' : 's'} procesado{results.length === 1 ? '' : 's'} con éxito.
        Tu saldo de fierros se actualizó.
      </p>
      <div className="flex justify-center gap-2">
        <Link to="/app/dashboard" className="no-underline">
          <Button variant="primary" size="md">
            Ir al panel
          </Button>
        </Link>
        <Link to="/planes" className="no-underline">
          <Button variant="secondary" size="md">
            Volver a planes
          </Button>
        </Link>
      </div>
    </div>
  );
}
